import React, { useState, useCallback } from 'react';
import Table, { TableBody, TableHeader } from '@ingka/table';
import Pill from '@ingka/pill';
import arrowDownToBase from '@ingka/ssr-icon/paths/arrow-down-to-base';
import './euTableLayout.scss';
import {
  getEUFEDSARefNumSubmitPDF,
  getEUFEDSADDSRefNumSubmit,
  getEUSupplierReferenceNumber,
} from 'utils/common';
import Tooltip from '@ingka/tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { downloadDDSPdf } from 'Action/euPendingApprovalAction';
import { AppDispatch, RootState } from 'Store/store';
import { ModalData } from 'components/Modal/modalData';
import Pagination from 'components/Pagination/pagination';
import Hyperlink from '@ingka/hyperlink';
import { getEuStatusList } from 'Action/euPendingApprovalAction';
import LocalTime from 'pages/LocalTime/localTime';

const EuTableLayout = (props: any) => {
  const {
    items,
    isLeftSide,
    euPendingApprovalData,
    selectedRow,
    resetAutoApprove,
    setComment,
    handleTabChange,
    setActiveTab,
    setSupplierDDSRefNum,
    setSelectedStatus,
    setHasSearched,
    setPDFInlineMessage,
    setFedsa_RefNum,
    selectedStatus,
  } = props;

  const headers = items?.length
    ? Object.keys(items[0]).filter((key) => !['__fullRefNum', '__Ref_Num'].includes(key))
    : [];
  const capitalizeHeader = (header) =>
    header
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  const MAX_ARTICLES_LENGTH = 20;
  const ITEMS_PER_PAGE = 7;
  const [highlightedRow, setHighlightedRow] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDetails, setModalDetails] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const dispatch = useDispatch<AppDispatch>();
  const { euSubmittedToEuList } = useSelector((state: RootState) => state.euSubmittedToEuList);
  const { euArchivedList } = useSelector((state: RootState) => state.euArchivedList);
  const downloadPdf = async (e: any, header: any, item: any, type: any) => {
    e.stopPropagation();

    const refNum = item.__fullRefNum || item.__Ref_Num;
    if (!refNum) {
      setPDFInlineMessage('Error: Missing reference number');
      return;
    }

    if (type === 'pdf') {
      let id;

      if (selectedStatus === 'IKEA') {
        id = getEUFEDSARefNumSubmitPDF(euPendingApprovalData, item['__fullRefNum']);
      } else if (selectedStatus === 'Supplier') {
        id = getEUFEDSARefNumSubmitPDF(euPendingApprovalData, item['__Ref_Num']);
      }

      if (id && id.fedsa_RefNum) {
        const result = await dispatch(downloadDDSPdf(id.fedsa_RefNum));
        if (result?.success === false) {
          setPDFInlineMessage('Error: PDF Download Failed');
        } else {
          setPDFInlineMessage('');
        }
      } else {
        setPDFInlineMessage('Error: Invalid reference data');
      }
    }
  };
  const renderTableDetails = (header, item) => {
    if (header === 'PDF') {
      return (
        <Pill
          size="xsmall"
          onClick={(e) => downloadPdf(e, header, item, 'pdf')}
          leadingIcon={arrowDownToBase}
        />
      );
    } else if (header === 'articles') {
      const articlesText = item[header];
      const showTooltip = articlesText?.length > MAX_ARTICLES_LENGTH;
      const displayText = showTooltip
        ? `${articlesText?.slice(0, MAX_ARTICLES_LENGTH)}...`
        : articlesText;
      return showTooltip ? (
        <Tooltip describedById="tooltip-example" position="top" tooltipText={articlesText}>
          <span>{displayText}</span>
        </Tooltip>
      ) : (
        displayText
      );
    } else if (header === 'DDS Received On') {
      return <LocalTime utcDateTime={item[header]} />;
    } else if (header === 'Date of Submission') {
      return <LocalTime utcDateTime={item[header]} />;
    } else if (header === 'commodities') {
      const articlesText = item[header];
      const showTooltip = articlesText?.length > MAX_ARTICLES_LENGTH;
      const displayText = showTooltip
        ? `${articlesText.slice(0, MAX_ARTICLES_LENGTH)}...`
        : articlesText;
      return showTooltip ? (
        <Tooltip describedById="tooltip-example" position="top" tooltipText={articlesText}>
          <span>{displayText}</span>
        </Tooltip>
      ) : (
        displayText
      );
    } else if (header === 'Source') {
      return item[header] === false ? 'File upload' : 'API';
    } else if (header === 'Link to Parent DDS') {
      return (
        <Hyperlink
          title="click to view supplierDDSRefNum"
          colour="grey"
          text={
            <span
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <img
                src="/icons8-link-48.png"
                alt="link icon"
                style={{ width: '22px', height: '22px' }}
              />
            </span>
          }
          onClick={(e) => {
            e.preventDefault();
            const selectedRow = getEUFEDSADDSRefNumSubmit(
              euSubmittedToEuList,
              item['DDS Reference Number']
            );
            if (selectedRow) {
              const supplierDDSRef = selectedRow.supplierDDSRefNum;
              dispatch(getEuStatusList('Supplier'));
              setSupplierDDSRefNum(supplierDDSRef);
              setSelectedStatus('Supplier');
              handleTabChange('Supplier');
              setActiveTab('tab_10');
            }
          }}
          url=""
        />
      );
    } else if (header === 'Link to IKEA DDS') {
      const selectedRow = getEUSupplierReferenceNumber(euArchivedList, item['Supplier Ref_Num']);
      const isLinkEnabled = !!selectedRow?.fedsa_RefNum;
      return (
        <Hyperlink
          title={!isLinkEnabled ? 'Submission Pending' : 'click to view FRN'}
          colour="grey"
          className={isLinkEnabled ? '' : 'link link--grey'}
          text={
            <span
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <img
                src="/icons8-link-48.png"
                alt="link icon"
                style={{ width: '22px', height: '22px' }}
              />
            </span>
          }
          disabled={!isLinkEnabled}
          onClick={(e) => {
            e.preventDefault();
            const selectedRow = getEUSupplierReferenceNumber(
              euArchivedList,
              item['Supplier Ref_Num']
            );

            const fedsaDDSRefNum = selectedRow?.fedsa_dds_RefNum;

            if (fedsaDDSRefNum) {
              const fedsa_RefNum = selectedRow.fedsa_RefNum;
              dispatch(getEuStatusList('IKEA'));
              setFedsa_RefNum(fedsa_RefNum);
              setSelectedStatus('IKEA');
              handleTabChange('IKEA');
              setActiveTab('tab_9');
            }
          }}
          url=""
        />
      );
    } else {
      return item[header];
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setHighlightedRow('');
  };

  const paginatedItems = items.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);

  return (
    <>
      <div style={{ width: '100%' }}></div>
      <div>
        <div style={{ marginTop: '10px', overflowY: 'auto' }}>
          <Table className="table" fullWidth>
            <TableHeader sticky>
              <tr>
                {headers.map((header, index) => (
                  <th key={index}>{capitalizeHeader(header)}</th>
                ))}
              </tr>
            </TableHeader>

            <TableBody striped>
              {paginatedItems.map((item, index) => (
                <tr key={index}>
                  {headers.map((header, idx) => {
                    return (
                      <td
                        key={idx}
                        style={{
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          maxWidth: 120,
                          cursor: header === 'Supplier Ref_Num' ? 'pointer' : 'default',
                        }}
                        title={
                          header === 'Ref_Num'
                            ? item['__fullRefNum'] || item['Ref_Num']
                            : header === '__Ref_Num'
                            ? item['__fullRefNum'] || item['__Ref_Num']
                            : header === 'Supplier Ref_Num'
                            ? item['Supplier Ref_Num']
                            : header === 'Current status'
                            ? item['Current status']
                            : undefined
                        }
                      >
                        {renderTableDetails(header, item)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            items={items}
          />
        )}
      </div>

      <div style={{ position: 'absolute' }}>
        {modalOpen && modalDetails && (
          <ModalData
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            modalDetails={modalDetails}
          />
        )}
      </div>
    </>
  );
};

export default EuTableLayout;
